import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Instagram, Facebook, Linkedin, MapPin, MessageCircle, Menu, X } from 'lucide-react';
import { getPublicPageBySlug, PublicPageData } from '../services/siteService';
import { HeroBlock } from '../components/site/blocks/HeroBlock';
import {
  AboutBlock,
  BenefitsBlock,
  StoryBlock,
  ImageTextBlock,
  TestimonialsBlock,
  CTABlock,
  FAQBlock,
  TextBlock,
  ImageBlock,
  VideoBlock,
  StepsBlock,
  WhatsAppBlock,
  CustomHTMLBlock,
} from '../components/site/blocks/ContentBlocks';
import {
  TimelineBlock,
  ProblemSolutionBlock,
  TeamBlock,
  ProductShowcaseBlock,
  AdvancedVideoBlock,
  BlockWrapper,
} from '../components/site/blocks/AdvancedBlocks';
import { FormBlock } from '../components/site/blocks/FormBlock';
import { usePageTracking } from '../hooks/usePageTracking';
import type {
  SitePageType,
  SiteBlock,
  BackgroundSettings,
  BlockSettings,
  HeroSettings,
  AboutSettings,
  BenefitsSettings,
  StorySettings,
  ImageTextSettings,
  TestimonialsSettings,
  FormBlockSettings,
  CTASettings,
  FAQSettings,
  TextSettings,
  ImageSettings,
  VideoSettings,
  StepsSettings,
  WhatsAppSettings,
  CustomHTMLSettings,
  TimelineSettings,
  ProblemSolutionSettings,
  TeamSettings,
  ProductShowcaseSettings,
  AdvancedVideoSettings,
} from '../types/site';
import { BLOCK_TYPE_LABELS } from '../types/site';
import type { Profile } from '../lib/supabase';
import { logger } from '../lib/logger';

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );
}

function SocialLinks({ profile, primaryColor }: { profile: Profile; primaryColor: string }) {
  const links = [
    { url: profile.instagram_url, icon: Instagram, label: 'Instagram' },
    { url: profile.tiktok_url, icon: TikTokIcon, label: 'TikTok' },
    { url: profile.facebook_url, icon: Facebook, label: 'Facebook' },
    { url: profile.linkedin_url, icon: Linkedin, label: 'LinkedIn' },
  ].filter((l) => l.url);

  if (links.length === 0) return null;

  return (
    <div className="flex items-center justify-center gap-3">
      {links.map(({ url, icon: Icon, label }) => (
        <a
          key={label}
          href={url!}
          target="_blank"
          rel="noopener noreferrer"
          className="w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg"
          style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
          title={label}
        >
          <Icon className="w-5 h-5" />
        </a>
      ))}
    </div>
  );
}

function ProfileHeader({ profile, primaryColor, blocks }: { profile: Profile; primaryColor: string; blocks: SiteBlock[] }) {
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const displayName = profile.first_name
    ? `${profile.first_name}${profile.last_name ? ' ' + profile.last_name : ''}`
    : profile.full_name;

  const hasLocation = profile.city || profile.country;
  const menuBlocks = blocks.filter(b => b.visible && b.show_in_menu);

  const MAX_VISIBLE_ITEMS = 4;
  const visibleMenuBlocks = menuBlocks.slice(0, MAX_VISIBLE_ITEMS);
  const moreMenuBlocks = menuBlocks.slice(MAX_VISIBLE_ITEMS);

  useEffect(() => {
    if (menuBlocks.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    menuBlocks.forEach((block) => {
      const element = document.getElementById(`block-${block.id}`);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [menuBlocks]);

  const getBlockLabel = (block: SiteBlock): string => {
    if (block.menu_label) return block.menu_label;
    return BLOCK_TYPE_LABELS[block.type] || block.type;
  };

  const scrollToBlock = (blockId: string) => {
    const element = document.getElementById(`block-${blockId}`);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
      setShowMoreDropdown(false);
      setShowMobileMenu(false);
    }
  };

  return (
    <>
      <section className="py-4 px-4 bg-white/80 backdrop-blur-sm sticky top-0 z-40 border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-6">
        <div className="flex items-center gap-4 flex-shrink-0">
          {profile.profile_image_url && (
            <div className="relative">
              <img
                src={profile.profile_image_url}
                alt={displayName || 'Profile'}
                className="w-11 h-11 rounded-xl object-cover ring-2 ring-white shadow-md"
              />
              <div
                className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white"
                style={{ backgroundColor: primaryColor }}
              />
            </div>
          )}
          <div>
            {displayName && (
              <h2 className="text-base font-bold text-gray-900">{displayName}</h2>
            )}
            {hasLocation && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {[profile.city, profile.country].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>

        {menuBlocks.length > 0 && (
          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {visibleMenuBlocks.map((block) => {
              const isActive = activeSection === `block-${block.id}`;
              return (
                <button
                  key={block.id}
                  onClick={() => scrollToBlock(block.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  style={
                    isActive
                      ? {
                          backgroundColor: `${primaryColor}12`,
                          color: primaryColor,
                        }
                      : {}
                  }
                >
                  {getBlockLabel(block)}
                </button>
              );
            })}

            {moreMenuBlocks.length > 0 && (
              <div
                className="relative"
                onMouseEnter={() => setShowMoreDropdown(true)}
                onMouseLeave={() => setShowMoreDropdown(false)}
              >
                <button
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1"
                >
                  Mehr
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showMoreDropdown && (
                  <div className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[200px]">
                    {moreMenuBlocks.map((block) => {
                      const isActive = activeSection === `block-${block.id}`;
                      return (
                        <button
                          key={block.id}
                          onClick={() => scrollToBlock(block.id)}
                          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                            isActive
                              ? ''
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                          style={
                            isActive
                              ? {
                                  backgroundColor: `${primaryColor}12`,
                                  color: primaryColor,
                                }
                              : {}
                          }
                        >
                          {getBlockLabel(block)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>
        )}

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="hidden md:flex">
            <SocialLinks profile={profile} primaryColor={primaryColor} />
          </div>

          {menuBlocks.length > 0 && (
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Menu"
            >
              {showMobileMenu ? (
                <X className="w-5 h-5 text-gray-600" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600" />
              )}
            </button>
          )}
        </div>
      </div>

      {showMobileMenu && menuBlocks.length > 0 && (
        <div className="lg:hidden border-t border-gray-100">
          <div className="max-w-7xl mx-auto py-3 px-4 space-y-1">
            {menuBlocks.map((block) => {
              const isActive = activeSection === `block-${block.id}`;
              return (
                <button
                  key={block.id}
                  onClick={() => scrollToBlock(block.id)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  style={
                    isActive
                      ? {
                          backgroundColor: `${primaryColor}12`,
                          color: primaryColor,
                        }
                      : {}
                  }
                >
                  {getBlockLabel(block)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
    </>
  );
}

function FloatingWhatsAppButton({ phone }: { phone: string; primaryColor: string }) {
  const phoneClean = phone.replace(/\D/g, '');

  return (
    <a
      href={`https://wa.me/${phoneClean}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-50"
      title="WhatsApp"
    >
      <MessageCircle className="w-7 h-7 text-white" />
    </a>
  );
}

function ImprintFooter({ imprintText }: { imprintText: string; primaryColor: string }) {
  const sanitizedText = imprintText
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '');

  return (
    <section className="py-16 px-4 bg-gray-50/80">
      <div className="max-w-3xl mx-auto">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6">Impressum</h3>
        <div className="text-sm text-gray-500 whitespace-pre-wrap leading-relaxed">{sanitizedText}</div>
      </div>
    </section>
  );
}

export function PublicSitePage() {
  const { slug, pageType } = useParams<{ slug: string; pageType: string }>();
  const [page, setPage] = useState<PublicPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const formRef = useRef<HTMLElement>(null);

  const type: SitePageType = pageType === 'products' ? 'PRODUCTS' : 'BUSINESS';

  usePageTracking({
    pageType: type,
    slug: slug || '',
    enabled: !isLoading && !notFound && !!page,
  });

  useEffect(() => {
    if (slug) {
      loadPage();
    }
  }, [slug, type]);

  useEffect(() => {
    if (page) {
      document.title = page.title;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', page.description);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = page.description;
        document.head.appendChild(meta);
      }
    }
  }, [page]);

  const loadPage = async () => {
    if (!slug) return;
    try {
      const data = await getPublicPageBySlug(slug, type);
      if (data) {
        setPage(data);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      logger.error('Error loading page:', error);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getBlockSettingsWithFallback = (block: SiteBlock): BlockSettings => {
    const settings = block.settings;
    const profile = page?.profile;

    if (!profile) return settings;

    if (block.type === 'ABOUT' && !(settings as AboutSettings).text) {
      return { ...settings, text: profile.short_bio || (settings as AboutSettings).text } as AboutSettings;
    }

    if (block.type === 'STORY' && !(settings as StorySettings).text) {
      return { ...settings, text: profile.story || (settings as StorySettings).text } as StorySettings;
    }

    return settings;
  };

  const renderBlock = (block: SiteBlock) => {
    if (!block.visible) return null;

    const settings = getBlockSettingsWithFallback(block);
    const primaryColor = page?.theme_primary_color || 'var(--color-primary)';
    const accentColor = page?.theme_accent_color || '#E8C6B8';
    const background = (settings as Record<string, unknown> & { background?: BackgroundSettings }).background;

    let blockContent: JSX.Element | null = null;

    switch (block.type) {
      case 'HERO':
        blockContent = (
          <BlockWrapper background={background}>
            <HeroBlock
              settings={settings as HeroSettings}
              primaryColor={primaryColor}
              onScrollToForm={scrollToForm}
            />
          </BlockWrapper>
        );
        break;

      case 'ABOUT':
        blockContent = (
          <BlockWrapper background={background}>
            <AboutBlock
              settings={settings as AboutSettings}
              primaryColor={primaryColor}
            />
          </BlockWrapper>
        );
        break;

      case 'BENEFITS':
      case 'HIGHLIGHTS':
        blockContent = (
          <BlockWrapper background={background}>
            <BenefitsBlock
              settings={settings as BenefitsSettings}
              primaryColor={primaryColor}
              accentColor={accentColor}
            />
          </BlockWrapper>
        );
        break;

      case 'STORY':
        blockContent = (
          <BlockWrapper background={background}>
            <StoryBlock
              settings={settings as StorySettings}
              primaryColor={primaryColor}
            />
          </BlockWrapper>
        );
        break;

      case 'IMAGE_TEXT':
        blockContent = (
          <BlockWrapper background={background}>
            <ImageTextBlock
              settings={settings as ImageTextSettings}
              primaryColor={primaryColor}
            />
          </BlockWrapper>
        );
        break;

      case 'TESTIMONIALS':
        blockContent = (
          <BlockWrapper background={background}>
            <TestimonialsBlock
              settings={settings as TestimonialsSettings}
              primaryColor={primaryColor}
              accentColor={accentColor}
            />
          </BlockWrapper>
        );
        break;

      case 'FORM':
        blockContent = (
          <BlockWrapper background={background}>
            <FormBlock
              ref={formRef}
              settings={settings as FormBlockSettings}
              primaryColor={primaryColor}
              originPageType={type}
              originSlug={slug || ''}
            />
          </BlockWrapper>
        );
        break;

      case 'CTA':
        blockContent = (
          <BlockWrapper background={background}>
            <CTABlock
              settings={settings as CTASettings}
              primaryColor={primaryColor}
              onScrollToForm={scrollToForm}
            />
          </BlockWrapper>
        );
        break;

      case 'FAQ':
        blockContent = (
          <BlockWrapper background={background}>
            <FAQBlock
              settings={settings as FAQSettings}
              primaryColor={primaryColor}
            />
          </BlockWrapper>
        );
        break;

      case 'TEXT':
        blockContent = (
          <BlockWrapper background={background}>
            <TextBlock settings={settings as TextSettings} />
          </BlockWrapper>
        );
        break;

      case 'IMAGE':
        blockContent = (
          <BlockWrapper background={background}>
            <ImageBlock settings={settings as ImageSettings} />
          </BlockWrapper>
        );
        break;

      case 'VIDEO':
        blockContent = (
          <BlockWrapper background={background}>
            <VideoBlock settings={settings as VideoSettings} />
          </BlockWrapper>
        );
        break;

      case 'STEPS':
        blockContent = (
          <BlockWrapper background={background}>
            <StepsBlock
              settings={settings as StepsSettings}
              primaryColor={primaryColor}
            />
          </BlockWrapper>
        );
        break;

      case 'WHATSAPP_BUTTON':
        blockContent = (
          <BlockWrapper background={background}>
            <WhatsAppBlock
              settings={settings as WhatsAppSettings}
              primaryColor={primaryColor}
            />
          </BlockWrapper>
        );
        break;

      case 'CUSTOM_HTML':
        blockContent = (
          <BlockWrapper background={background}>
            <CustomHTMLBlock settings={settings as CustomHTMLSettings} />
          </BlockWrapper>
        );
        break;

      case 'TIMELINE':
        blockContent = (
          <TimelineBlock
            settings={settings as TimelineSettings}
            primaryColor={primaryColor}
          />
        );
        break;

      case 'PROBLEM_SOLUTION':
        blockContent = (
          <ProblemSolutionBlock
            settings={settings as ProblemSolutionSettings}
            primaryColor={primaryColor}
          />
        );
        break;

      case 'TEAM':
        blockContent = (
          <TeamBlock
            settings={settings as TeamSettings}
            primaryColor={primaryColor}
          />
        );
        break;

      case 'PRODUCT_SHOWCASE':
        blockContent = (
          <ProductShowcaseBlock
            settings={settings as ProductShowcaseSettings}
            primaryColor={primaryColor}
          />
        );
        break;

      case 'ADVANCED_VIDEO':
        blockContent = (
          <AdvancedVideoBlock
            settings={settings as AdvancedVideoSettings}
            primaryColor={primaryColor}
          />
        );
        break;

      default:
        return null;
    }

    return (
      <div key={block.id} id={`block-${block.id}`}>
        {blockContent}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Seite nicht verfügbar
          </h1>
          <p className="text-gray-500">
            Diese Seite existiert nicht oder ist nicht mehr verfügbar.
          </p>
        </div>
      </div>
    );
  }

  const primaryColor = page.theme_primary_color || 'var(--color-primary)';
  const profile = page.profile;
  const showProfileHeader = profile && (profile.profile_image_url || profile.first_name || profile.full_name);
  const showFloatingWhatsApp = profile?.phone && !page.blocks.some((b) => b.type === 'WHATSAPP_BUTTON' && b.visible);

  return (
    <>
      <div className="min-h-screen bg-white">
        {showProfileHeader && (
          <ProfileHeader
            profile={profile!}
            primaryColor={primaryColor}
            blocks={page.blocks.sort((a, b) => a.block_order - b.block_order)}
          />
        )}

        {page.blocks
          .sort((a, b) => a.block_order - b.block_order)
          .map(renderBlock)}

        {profile?.imprint_text && (
          <ImprintFooter imprintText={profile.imprint_text} primaryColor={primaryColor} />
        )}

        <footer className="py-12 px-4 bg-white border-t border-gray-100">
          <div className="max-w-6xl mx-auto">
            {profile && (
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-3">
                  {profile.profile_image_url && (
                    <img
                      src={profile.profile_image_url}
                      alt=""
                      className="w-10 h-10 rounded-xl object-cover"
                    />
                  )}
                  <span className="font-medium text-gray-900">
                    {profile.first_name || profile.full_name}
                  </span>
                </div>
                <SocialLinks profile={profile} primaryColor={primaryColor} />
              </div>
            )}
            <div className="mt-8 pt-8 border-t border-gray-100 text-center">
              <p className="text-sm text-gray-400">Powered by MAMAL</p>
            </div>
          </div>
        </footer>

        {showFloatingWhatsApp && (
          <FloatingWhatsAppButton phone={profile!.phone!} primaryColor={primaryColor} />
        )}
      </div>
    </>
  );
}
